import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import { createCTScanRecord, fetchCTScanRecords, fetchCTScanTests } from "../../../../../api/ct_scan";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/$/, "");
const MEDIA_BASE = (process.env.REACT_APP_MEDIA_URL || API.replace(/\/api$/, "")).replace(/\/$/, "");
const formInputSx = {
  "& .MuiInputBase-input": { py: 1.45, px: 1.75 },
  "& .MuiInputBase-inputMultiline": { py: 1.2, px: 1.75 },
};

const toAbsoluteMediaUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  return `${MEDIA_BASE}${value.startsWith("/") ? value : `/${value}`}`;
};

const isRowPaid = (row) => {
  if (!row || typeof row !== "object") return false;
  if (typeof row.is_paid === "boolean") return row.is_paid;
  if (typeof row.paid === "boolean") return row.paid;
  if (typeof row.payment_status === "number") return row.payment_status === 2 || row.payment_status === 1;
  if (typeof row.payment_status === "string") {
    const status = row.payment_status.toLowerCase();
    return status === "paid" || status === "completed" || status === "success";
  }
  return false;
};

const isRowUrgent = (row) => {
  if (!row || typeof row !== "object") return false;
  if (typeof row.is_urgent === "boolean") return row.is_urgent;
  if (typeof row.urgent === "boolean") return row.urgent;
  if (typeof row.priority === "string") {
    const p = row.priority.toLowerCase();
    return p === "urgent" || p === "high";
  }
  if (typeof row.is_urgent === "number") return row.is_urgent === 1;
  if (typeof row.urgent === "number") return row.urgent === 1;
  return false;
};

const CTScanReferral = () => {
  const { t } = useTranslation();
  const token = useAuth()?.user?.token;
  const headers = token ? { Authorization: `Token ${token}` } : {};

  const [records, setRecords] = useState([]);
  const [tests, setTests] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketSearch, setTicketSearch] = useState("");
  const [ticketOpen, setTicketOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [form, setForm] = useState({
    ticket: "",
    patient: "",
    patient_name: "",
    test: "",
    amount: "",
    discount: "",
    is_urgent: false,
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [resultOpen, setResultOpen] = useState(false);
  const [selectedResultRow, setSelectedResultRow] = useState(null);

  const load = async () => {
    const [rec, tst, tkt] = await Promise.all([
      fetchCTScanRecords(token),
      fetchCTScanTests(token),
      axios.get(`${API}/tickets/`, { headers }).then((r) => r.data),
    ]);
    setRecords(Array.isArray(rec) ? rec : []);
    setTests(Array.isArray(tst) ? tst : []);
    setTickets(tkt?.results || tkt || []);
  };

  useEffect(() => {
    if (token) load();
  }, [token]);

  const handleTicketChange = (ticketId) => {
    const tk = tickets.find((t) => String(t.id) === String(ticketId));
    setForm((prev) => ({ ...prev, ticket: ticketId, patient: tk?.patient || "" }));
  };

  const handleTestChange = (testId) => {
    const selected = tests.find((t) => String(t.id) === String(testId));
    const testAmount = selected?.amount ?? selected?.price ?? "";
    setForm((prev) => ({ ...prev, test: testId, amount: testAmount }));
  };

  const handleSubmit = async () => {
    if (!form.ticket || !form.patient || !form.test || !form.date) return;
    const amount = form.amount === "" ? 0 : Number(form.amount);
    const discount = form.discount === "" ? 0 : Number(form.discount);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    const safeDiscount = Number.isFinite(discount) ? discount : 0;
    const netAmount = Math.max(0, safeAmount - safeDiscount);
    try {
      await createCTScanRecord(token, {
        patient: Number(form.patient),
        ticket: Number(form.ticket),
        test: Number(form.test),
        amount: netAmount,
        total_amount: netAmount,
        discount: safeDiscount,
        discount_amount: safeDiscount,
        is_urgent: !!form.is_urgent,
        date: form.date,
        notes: form.notes || "",
        status: "Pending",
      });
      setForm({
        ticket: "",
        patient: "",
        patient_name: "",
        test: "",
        amount: "",
        discount: "",
        is_urgent: false,
        date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setTicketSearch("");
      setTicketOpen(false);
      await load();
      setSnackbar({ open: true, message: "Data added successfully!", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: t("doctor.failedToSubmitCTScanReferral", "Failed to submit CT Scan referral."), severity: "error" });
    }
  };

  return (
    <>
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t("doctor.ctScanReferralForm", "CT Scan Referral Form")}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("doctor.ticketNumber", "Ticket Number")}
              placeholder={t("doctor.searchByTicketNumber", "Search by ticket number...")}
              value={ticketSearch}
              onFocus={() => setTicketOpen(true)}
              onChange={(e) => {
                const val = e.target.value;
                setTicketSearch(val);
                setForm({ ...form, ticket: "", patient: "", test: "" });
                setTicketOpen(true);
              }}
              helperText={
                ticketSearch.length < 3
                  ? t("doctor.typeAtLeast3Chars", "Type at least 3 characters to search")
                  : !form.ticket
                    ? t("doctor.selectTicketFromResults", "Please select a ticket from results")
                    : " "
              }
              error={ticketSearch.length >= 3 && !form.ticket}
              size="small"
              sx={formInputSx}
            />
            {ticketOpen && ticketSearch.length >= 3 && (
              <Paper sx={{ mt: 1, maxHeight: 220, overflowY: "auto", border: "1px solid #ddd" }}>
                {tickets
                  .filter((tk) =>
                    (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())
                  )
                  .map((tk) => (
                    <MenuItem
                      key={tk.id}
                      onClick={() => {
                        setTicketSearch(tk.ticket_number || "");
                        setForm({
                          ...form,
                          ticket: tk.id,
                          patient: tk.patient || "",
                          patient_name: tk.patient_name || tk.patient_name_display || tk.patient_display?.name || "",
                          test: "",
                        });
                        setTicketOpen(false);
                      }}
                    >
                      <Stack>
                        <Typography fontWeight={600}>{tk.ticket_number}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(tk.patient_name || tk.patient_name_display || t("doctor.unknownPatient", "Unknown Patient"))} ({`${t("doctor.patientID", "Patient ID")}: ${tk.patient || "-"}`})
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                {tickets.filter((tk) => (tk.ticket_number || "").toLowerCase().includes(ticketSearch.toLowerCase())).length === 0 && (
                  <Typography sx={{ p: 1.5, color: "text.secondary" }}>{t("doctor.noTicketsFound", "No tickets found")}</Typography>
                )}
              </Paper>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("doctor.patient", "Patient")}
              value={form.patient_name || tickets.find((tk) => String(tk.id) === String(form.ticket))?.patient_name || tickets.find((tk) => String(tk.id) === String(form.ticket))?.patient_name_display || ""}
              InputProps={{ readOnly: true }}
              size="small"
              sx={formInputSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth size="small" label={t("doctor.selectTest", "Select Test")} value={form.test} onChange={(e) => handleTestChange(e.target.value)} sx={formInputSx}>
              {tests.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small" type="date" label={t("doctor.date", "Date")} InputLabelProps={{ shrink: true }} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} sx={formInputSx} />
          </Grid>
        <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t("doctor.amount", "Amount")}
              value={form.amount}
              InputProps={{ readOnly: true }}
              sx={formInputSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t("doctor.discount", "Discount")}
              value={form.discount}
              onChange={(e) => setForm({ ...form, discount: e.target.value })}
              sx={formInputSx}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.is_urgent}
                  onChange={(e) => setForm({ ...form, is_urgent: e.target.checked })}
                />
              }
              label={t("doctor.urgent", "Urgent")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" multiline minRows={3} label={t("doctor.notes", "Notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={formInputSx} />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleSubmit}
              disabled={!form.ticket || !form.patient || !form.test}
              sx={{ bgcolor: "#1f6f8b", "&:hover": { bgcolor: "#185b72" }, textTransform: "none", fontWeight: 700 }}
            >
              {t("doctor.submit", "Submit")}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t("doctor.ctScanReferrals", "CT Scan Referrals")}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#3f8aa1" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.patient", "Patient")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.testType", "Test Type")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.status", "Status")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.orderDate", "Order Date")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.totalAmount", "Total Amount")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.urgent", "Urgent")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.paid", "Paid")}</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700 }}>{t("doctor.result", "Result")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    bgcolor: row.status === "In Review" ? "#b7e5ea" : "#fff",
                    "&:hover": { bgcolor: "#eef7fa" },
                  }}
                >
                  <TableCell>{row.patient_display?.name || "-"}</TableCell>
                  <TableCell>
                    {row.test_display?.name ||
                      tests.find((t) => String(t.id) === String(row.test))?.name ||
                      "-"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status || "Pending"}
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        color:
                          row.status === "Completed" ? "#2e7d32" :
                          row.status === "Pending" ? "#ef6c00" :
                          row.status === "Canceled" ? "#d32f2f" : "#455a64",
                        borderColor:
                          row.status === "Completed" ? "#66bb6a" :
                          row.status === "Pending" ? "#ffb74d" :
                          row.status === "Canceled" ? "#ef9a9a" : "#b0bec5",
                      }}
                    />
                  </TableCell>
                  <TableCell>{row.date ? new Date(row.date).toLocaleDateString("en-GB") : "-"}</TableCell>
                  <TableCell>
                    {(() => {
                      const testId = row.test_display?.id || row.test;
                      const test = tests.find((t) => String(t.id) === String(testId));
                      const rowAmount = Number(row.amount);
                      if (Number.isFinite(rowAmount)) return rowAmount.toFixed(2);
                      const baseAmount = Number(test?.price ?? test?.cost ?? 0);
                      const discount = Number(row.discount ?? row.discount_amount ?? 0);
                      if (!Number.isFinite(baseAmount)) return "-";
                      return Math.max(0, baseAmount - (Number.isFinite(discount) ? discount : 0)).toFixed(2);
                    })()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isRowUrgent(row) ? t("doctor.yes", "Yes") : t("doctor.no", "No")}
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        borderColor: isRowUrgent(row) ? "#66bb6a" : "#b0b0b0",
                        color: isRowUrgent(row) ? "#2e7d32" : "#333",
                        minWidth: 44,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={isRowPaid(row) ? t("doctor.yes", "Yes") : t("doctor.no", "No")}
                      variant="outlined"
                      sx={{
                        fontWeight: 700,
                        borderColor: isRowPaid(row) ? "#66bb6a" : "#ef5350",
                        color: isRowPaid(row) ? "#2e7d32" : "#e53935",
                        minWidth: 44,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {row.report_image ? (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: "none", borderColor: "#7fb4c6", color: "#0b5b7c" }}
                        onClick={() => {
                          setSelectedResultRow(row);
                          setResultOpen(true);
                        }}
                      >
                        {t("doctor.viewResult", "View Result")}
                      </Button>
                    ) : (
                      <Typography color="text.secondary">{t("doctor.noResult", "No Result")}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog open={resultOpen} onClose={() => setResultOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t("doctor.ctScanResult", "CT Scan Result")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              fullWidth
              label={t("doctor.testAppointment", "Test Appointment")}
              value={selectedResultRow?.test_result || "-"}
              InputProps={{ readOnly: true }}
            />
            {selectedResultRow?.report_image ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                  {t("doctor.reportImage", "Report Image")}
                </Typography>
                <img
                  src={toAbsoluteMediaUrl(selectedResultRow.report_image)}
                  alt={t("doctor.ctScanResult", "CT Scan Result")}
                  style={{ width: "100%", maxheight: 520, objectFit: "contain", border: "1px solid #ddd", borderRadius: 8 }}
                />
              </Box>
            ) : (
              <Typography color="text.secondary">{t("doctor.noReportImage", "No report image")}</Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>{t("doctor.close", "Close")}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
    <Snackbar
      open={snackbar.open}
      autoHideDuration={5000}
      onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Paper
        elevation={4}
        sx={{
          width: 338,
          borderRadius: "6px",
          bgcolor: "#f3f3f3",
          overflow: "hidden",
          "@keyframes ctScanToastProgressLtr": {
            from: { transform: "scaleX(1)" },
            to: { transform: "scaleX(0)" },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 1.45, py: 0.82 }}>
          <CheckCircleIcon
            sx={{
              color: snackbar.severity === "success" ? "#17b629" : "#d32f2f",
              mr: 1,
              fontSize: 20,
            }}
          />
          <Typography
            sx={{ color: "#666", fontSize: "0.9rem", flexGrow: 1, lineHeight: 1.2 }}
          >
            {snackbar.message}
          </Typography>
          <CloseIcon
            onClick={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            sx={{ color: "#9a9a9a", cursor: "pointer", fontSize: 20 }}
          />
        </Box>
        <Box
          sx={{
            height: 5,
            bgcolor: snackbar.severity === "success" ? "#22c841" : "#d32f2f",
            width: "100%",
            transform: "scaleX(1)",
            transformOrigin: "left",
            animation: "ctScanToastProgressLtr 5s linear forwards",
          }}
        />
      </Paper>
    </Snackbar>
    </>
  );
};

export default CTScanReferral;



















